import React from 'react';
import axios from 'axios';
import {host, token} from '../assets/options';
import {REGION_DATA, AGE_RANGES, EMPLOYMENT_OPTIONS} from '../constants';

const APP_DOMAIN = 'aygi.am';
const SURVEY_COMPLETED_KEY = 'aygi_survey_completed';
const SURVEY_SKIPPED_KEY = 'aygi_survey_skipped';

const DEFAULT_CONFIG = {
    title: 'Օգնեք մեզ կայքը դարձնել Ձեզ համար ավելի օգտակար և հասանելի',
    fields: [
        {key: 'age', label: 'Տարիք', type: 'select', options: AGE_RANGES, required: true, placeholder: '', order: 0},
        {key: 'gender', label: 'Սեռ', type: 'select', options: ['Արական', 'Իգական'], required: true, placeholder: '', order: 1},
        {key: 'location', label: 'Բնակավայր', type: 'location', options: [], required: true, placeholder: '', order: 2},
        {key: 'agriActivity', label: 'Արդյո՞ք ունեք այգի և/կամ զբաղվում եք գյուղատնտեսությամբ', type: 'select', options: EMPLOYMENT_OPTIONS, required: true, placeholder: '', order: 3},
        {key: 'email', label: 'Էլ. հասցե', type: 'email', options: [], required: false, placeholder: 'example@mail.com', order: 4},
    ]
};

class SurveyPopup extends React.Component {
    _isMounted = false;
    timeoutId = null;

    constructor(props) {
        super(props);
        this.state = {
            showPopup: false,
            submitted: false,
            isSubmitting: false,
            error: '',
            fieldErrors: {},
            formData: {},
            config: null,
            configLoaded: false
        };
    }

    getSurveyCookie() {
        return document.cookie.split(';').some(c => c.trim().startsWith(`${SURVEY_COMPLETED_KEY}=`));
    }

    setSurveyCookie() {
        const maxAge = 60 * 60 * 24 * 365; // 1 year
        const isProduction = window.location.hostname.includes(APP_DOMAIN);
        const domainPart = isProduction ? `domain=.${APP_DOMAIN}; ` : '';
        document.cookie = `${SURVEY_COMPLETED_KEY}=true; ${domainPart}path=/; max-age=${maxAge}; SameSite=Lax`;
    }

    componentDidMount() {
        this._isMounted = true;
        if (this.getSurveyCookie()) return;

        const wasCompletedInLocalStorage = localStorage.getItem(SURVEY_COMPLETED_KEY);
        if (wasCompletedInLocalStorage) {
            // Migrate old localStorage-only completions to a shared cookie so both
            // aygi.am and www.aygi.am honour the completed state.
            this.setSurveyCookie();
            localStorage.removeItem(SURVEY_COMPLETED_KEY);
            return;
        }

        if (!sessionStorage.getItem(SURVEY_SKIPPED_KEY)) {
            this.loadConfig();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
        document.body.style.overflow = 'auto';
    }

    loadConfig() {
        axios.get(`${host}/api/survey/config`, {headers: {'authorization': token}})
            .then(res => {
                if (res.status === 200 && this._isMounted) {
                    this.initForm(res.data.data);
                }
            })
            .catch(() => {
                if (this._isMounted) {
                    this.initForm(DEFAULT_CONFIG);
                }
            });
    }

    initForm(config) {
        const formData = {};
        (config.fields || []).forEach(field => {
            if (field.type === 'location') {
                formData[field.key + '_region'] = '';
                formData[field.key + '_community'] = '';
                formData[field.key + '_settlement'] = '';
            } else {
                formData[field.key] = '';
            }
        });

        this.setState({config, configLoaded: true, formData}, () => {
            if (this.timeoutId) clearTimeout(this.timeoutId);
            this.timeoutId = setTimeout(() => {
                if (this._isMounted) {
                    this.setState({showPopup: true});
                    document.body.style.overflow = 'hidden';
                }
            }, 1200);
        });
    }

    handleInputChange = (e) => {
        const {name, value} = e.target;
        this.setState(prevState => {
            const newFieldErrors = {...prevState.fieldErrors};
            delete newFieldErrors[name];

            const updates = {[name]: value};

            if (name.endsWith('_region')) {
                const prefix = name.replace('_region', '');
                const communities = Object.keys(REGION_DATA[value] || {});
                updates[prefix + '_community'] = communities.length === 1 ? communities[0] : '';
                updates[prefix + '_settlement'] = '';
                if (communities.length === 1) delete newFieldErrors[prefix + '_community'];
            } else if (name.endsWith('_community')) {
                const prefix = name.replace('_community', '');
                updates[prefix + '_settlement'] = '';
            }

            return {
                formData: {...prevState.formData, ...updates},
                error: '',
                fieldErrors: newFieldErrors
            };
        });
    }

    closePopup = () => {
        this.setState({showPopup: false});
        document.body.style.overflow = 'auto';
    }

    skipSurvey = () => {
        sessionStorage.setItem(SURVEY_SKIPPED_KEY, 'true');
        this.closePopup();
    }

    handleSubmit = async (e) => {
        e.preventDefault();
        const {formData, config} = this.state;
        const fields = config ? config.fields : [];

        const newFieldErrors = {};
        fields.forEach(field => {
            if (field.required) {
                if (field.type === 'location') {
                    if (!formData[field.key + '_region']) newFieldErrors[field.key + '_region'] = true;
                    if (!formData[field.key + '_community']) newFieldErrors[field.key + '_community'] = true;
                    if (!formData[field.key + '_settlement']) newFieldErrors[field.key + '_settlement'] = true;
                } else {
                    if (!formData[field.key]) {
                        newFieldErrors[field.key] = true;
                    }
                }
            }
            if (field.type === 'email' && formData[field.key]) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(formData[field.key])) {
                    newFieldErrors[field.key] = true;
                }
            }
        });

        if (Object.keys(newFieldErrors).length > 0) {
            this.setState({
                fieldErrors: newFieldErrors,
                error: 'Խնդրում ենք լրացնել բոլոր պարտադիր դաշտերը:'
            });
            return;
        }

        this.setState({isSubmitting: true});

        try {
            await axios.post(`${host}/api/survey/`, {
                answers: formData,
                page: window.location.pathname
            }, {headers: {'authorization': token}});

            this.setSurveyCookie();
            this.setState({submitted: true, isSubmitting: false});
        } catch (err) {
            this.setState({
                isSubmitting: false,
                error: 'Տեղի է ունեցել սխալ: Խնդրում ենք փորձել կրկին:'
            });
        }
    }

    renderSelectField(field) {
        const {formData, fieldErrors} = this.state;
        return (
            <div key={field.key}
                 className={`survey-field${fieldErrors[field.key] ? ' has-error' : ''}`}>
                <label>{field.label}{field.required ? ' *' : ''}</label>
                <select name={field.key} value={formData[field.key] || ''}
                        onChange={this.handleInputChange}>
                    <option value="" disabled hidden>Ընտրել</option>
                    {(field.options || []).map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            </div>
        );
    }

    renderTextField(field) {
        const {formData, fieldErrors} = this.state;
        return (
            <div key={field.key}
                 className={`survey-field${fieldErrors[field.key] ? ' has-error' : ''}`}>
                <label>{field.label}{field.required ? ' *' : ''}</label>
                <input
                    type="text"
                    name={field.key}
                    placeholder={field.placeholder || ''}
                    value={formData[field.key] || ''}
                    onChange={this.handleInputChange}
                />
            </div>
        );
    }

    renderEmailField(field) {
        const {formData, fieldErrors} = this.state;
        return (
            <div key={field.key} className={`survey-field${fieldErrors[field.key] ? ' has-error' : ''}`}>
                <label className="email-label">
                    {field.label}
                    {!field.required && <span className="optional-text"> (Ոչ պարտադիր)</span>}
                    {field.required && ' *'}
                </label>
                <input
                    type="email"
                    name={field.key}
                    placeholder={field.placeholder || 'example@mail.com'}
                    value={formData[field.key] || ''}
                    onChange={this.handleInputChange}
                />
            </div>
        );
    }

    renderLocationGridPart(field) {
        const {formData, fieldErrors} = this.state;
        const regionKey = field.key + '_region';
        const communityKey = field.key + '_community';

        const selectedRegion = formData[regionKey] || '';
        const communities = selectedRegion ? Object.keys(REGION_DATA[selectedRegion] || {}) : [];
        const singleCommunity = communities.length === 1;

        return (
            <React.Fragment key={field.key + '_grid'}>
                <div
                    className={`survey-field${fieldErrors[regionKey] ? ' has-error' : ''}`}
                    style={singleCommunity ? {gridColumn: '1 / -1'} : undefined}
                >
                    <label>{'Մարզ (վարչատարածք)'}{field.required ? ' *' : ''}</label>
                    <select name={regionKey} value={selectedRegion} onChange={this.handleInputChange}>
                        <option value="" disabled hidden>Ընտրել</option>
                        {Object.keys(REGION_DATA).map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>

                {!singleCommunity && (
                    <div className={`survey-field${fieldErrors[communityKey] ? ' has-error' : ''}`}>
                        <label>{'Համայնք'}{field.required ? ' *' : ''}</label>
                        <select
                            name={communityKey}
                            value={formData[communityKey] || ''}
                            onChange={this.handleInputChange}
                            disabled={!selectedRegion}
                        >
                            <option value="" disabled hidden>Ընտրել</option>
                            {communities.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                )}
            </React.Fragment>
        );
    }

    renderLocationSettlement(field) {
        const {formData, fieldErrors} = this.state;
        const regionKey = field.key + '_region';
        const communityKey = field.key + '_community';
        const settlementKey = field.key + '_settlement';

        const selectedRegion = formData[regionKey] || '';
        const selectedCommunity = formData[communityKey] || '';
        const settlements = (selectedRegion && selectedCommunity)
            ? REGION_DATA[selectedRegion][selectedCommunity] || []
            : [];

        return (
            <div key={field.key + '_settlement'} className={`survey-field${fieldErrors[settlementKey] ? ' has-error' : ''}`}>
                <label>{'Քաղաք / Գյուղ / Բնակավայր'}{field.required ? ' *' : ''}</label>
                <select
                    name={settlementKey}
                    value={formData[settlementKey] || ''}
                    onChange={this.handleInputChange}
                    disabled={!selectedCommunity}
                >
                    <option value="" disabled hidden>Ընտրել</option>
                    {settlements.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>
        );
    }

    render() {
        const {showPopup, submitted, isSubmitting, error, config} = this.state;

        if (!showPopup) return null;

        const fields = config ? config.fields : [];
        const title = config ? config.title : '';

        const gridItems = [];
        const fullWidthItems = [];
        let passedLocation = false;

        fields.forEach(field => {
            if (field.type === 'location') {
                passedLocation = true;
                gridItems.push({...field, _render: 'locationGrid'});
                fullWidthItems.push({...field, _render: 'locationSettlement'});
            } else if (passedLocation || field.type === 'email') {
                fullWidthItems.push(field);
            } else {
                gridItems.push(field);
            }
        });

        return (
            <div className="survey-overlay">
                <div className="survey-modal" onClick={(e) => e.stopPropagation()}>
                    {!submitted ? (
                        <>
                            <div className="survey-header">
                                <h2>{title}</h2>
                            </div>

                            <form onSubmit={this.handleSubmit} className="survey-form">
                                <div className="survey-grid">
                                    {gridItems.map(item => {
                                        if (item._render === 'locationGrid') return this.renderLocationGridPart(item);
                                        if (item.type === 'select') return this.renderSelectField(item);
                                        if (item.type === 'text') return this.renderTextField(item);
                                        return null;
                                    })}
                                </div>

                                {fullWidthItems.map(item => {
                                    if (item._render === 'locationSettlement') return this.renderLocationSettlement(item);
                                    if (item.type === 'select') return this.renderSelectField(item);
                                    if (item.type === 'text') return this.renderTextField(item);
                                    if (item.type === 'email') return this.renderEmailField(item);
                                    return null;
                                })}

                                {error && <p className="survey-error-bubble">{error}</p>}

                                <div className="survey-actions">
                                    <button type="submit" className="btn-submit" disabled={isSubmitting}>
                                        {isSubmitting ? 'Ուղարկվում է...' : 'Ուղարկել'}
                                    </button>
                                </div>
                            </form>
                        </>
                    ) : (
                        <div className="survey-thanks">
                            <div className="thanks-icon">✓</div>
                            <h2>Շնորհակալություն աջակցության համար</h2>
                            <p className="contact-note">
                                Հարցերի և առաջարկների համար կարող եք գրել՝{' '}
                                <strong style={{ marginLeft: '4px' }}>aygi@icare.am</strong>
                            </p>
                            <button onClick={this.closePopup} className="btn-submit">
                                Փակել
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default SurveyPopup;
